import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ArrowRightFromSquare,
  ArrowUpFromLine,
  Box,
  Car,
  ChartColumn,
  Clock,
  FileText,
  House,
  Person,
  Persons,
  Receipt,
} from '@gravity-ui/icons'
import { Dropdown, Header, Label, Separator } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { systemApi } from '@/api/system.api'
import { UserAvatar } from '@/components/common/UserAvatar'
import { SidebarItem } from '@/components/ui/SidebarItem'
import { useAuthStore } from '@/store/auth.store'
import { useLayoutStore } from '@/store/layout.store'
import { useReportsStore } from '@/store/reports.store'
import { useThemeStore } from '@/store/theme.store'

const APP_VERSION = import.meta.env.VITE_VERSION

interface NavItem {
  to: string;
  labelKey: string;
  icon: React.ReactNode;
  badge?: boolean;
}

interface NavGroup {
  groupLabelKey?: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      {
        to: '/dashboard',
        labelKey: 'nav.dashboard',
        icon: <House width={16} height={16} />,
      },
    ],
  },
  {
    groupLabelKey: 'nav.group_operations',
    items: [
      {
        to: '/reports',
        labelKey: 'nav.reports',
        icon: <FileText width={16} height={16} />,
        badge: true,
      },
      {
        to: '/timesheet',
        labelKey: 'nav.timesheet',
        icon: <Clock width={16} height={16} />,
      },
      {
        to: '/settlements',
        labelKey: 'nav.settlements',
        icon: <Receipt width={16} height={16} />,
      },
    ],
  },
  {
    groupLabelKey: 'nav.group_warehouse',
    items: [
      {
        to: '/warehouse',
        labelKey: 'nav.warehouse',
        icon: <Box width={16} height={16} />,
        badge: false,
      },
    ],
  },
  {
    groupLabelKey: 'nav.group_provisioning',
    items: [
      {
        to: '/drivers',
        labelKey: 'nav.drivers',
        icon: <Person width={16} height={16} />,
      },
      {
        to: '/vehicles',
        labelKey: 'nav.vehicles',
        icon: <Car width={16} height={16} />,
      },
    ],
  },
  {
    groupLabelKey: 'nav.group_other',
    items: [
      {
        to: '/uploads',
        labelKey: 'nav.uploads',
        icon: <ArrowUpFromLine width={16} height={16} />,
      },
      {
        to: '/analytics',
        labelKey: 'nav.analytics',
        icon: <ChartColumn width={16} height={16} />,
      },
      // {
      //   to: '/vehicle-analytics',
      //   labelKey: 'nav.vehicle_analytics',
      //   icon: <ChartColumnStacked width={16} height={16} />,
      // },
      {
        to: '/users',
        labelKey: 'nav.users',
        icon: <Persons width={16} height={16} />,
      },
    ],
  },
]

export function Sidebar() {
  const { t } = useTranslation()
  const { dark, themeMode, setThemeMode } = useThemeStore()
  const { pagination } = useReportsStore()
  const sidebarCollapsed = useLayoutStore(s => s.sidebarCollapsed)
  const user = useAuthStore(s => s.user)
  const [beVersion, setBeVersion] = useState<string | null>(null)

  useEffect(() => {
    systemApi.getBuildInfo()
      .then(info => setBeVersion(info.Version))
      .catch(() => { /* silently ignore */
      })
  }, [])

  const handleLogout = useCallback(() => {
    useAuthStore.getState().logout()
  }, [])

  const version = useMemo(() => {
    if (!APP_VERSION && !beVersion) {
      return null
    }
    return (
      <div className="text-center text-xs text-foreground/30 py-2 font-mono">
        {APP_VERSION}{beVersion && ` (${beVersion})`}
      </div>
    )
  }, [beVersion])

  const userInfo = useMemo(() => {
    if (!user) {
      return null
    }

    const handleThemeChange = (keys: any) => {
      const selected = Array.from(keys)[0] as 'system' | 'light' | 'dark'
      if (selected) {
        setThemeMode(selected)
      }
    }

    return (
      <Dropdown>
        <Dropdown.Trigger className={'w-full'}>
          <button
            className="w-full text-left px-2 py-3 flex items-center gap-3 bg-zinc-500/5 dark:bg-white/5 border border-zinc-100 dark:border-white/5 rounded-lg shrink-0 cursor-pointer hover:bg-zinc-500/10 dark:hover:bg-white/10 transition-colors"
          >
            <UserAvatar name={user.Name} size="sm" className="shrink-0" />
            <div className="flex-1 min-w-0 w-full">
              <div className="text-sm font-medium text-foreground truncate" title={user.Name}>
                {user.Name}
              </div>
              <div className="text-[11px] text-foreground/45 truncate" title={user.Email}>
                {user.Email}
              </div>
            </div>
          </button>
        </Dropdown.Trigger>
        <Dropdown.Popover className="min-w-[220px]">
          <Dropdown.Menu aria-label="User menu">
            <Dropdown.Section
              selectedKeys={new Set([themeMode])}
              selectionMode="single"
              onSelectionChange={handleThemeChange}
            >
              <Header>{t('common.theme', 'Тема')}</Header>
              <Dropdown.Item id="system" textValue={t('common.system_theme', 'Системная')}>
                <Dropdown.ItemIndicator type="dot" />
                <Label>{t('common.system_theme', 'Системная')}</Label>
              </Dropdown.Item>
              <Dropdown.Item id="light" textValue={t('common.light_theme', 'Светлая тема')}>
                <Dropdown.ItemIndicator type="dot" />
                <Label>{t('common.light_theme', 'Светлая тема')}</Label>
              </Dropdown.Item>
              <Dropdown.Item id="dark" textValue={t('common.dark_theme', 'Тёмная тема')}>
                <Dropdown.ItemIndicator type="dot" />
                <Label>{t('common.dark_theme', 'Тёмная тема')}</Label>
              </Dropdown.Item>
            </Dropdown.Section>
            <Separator />
            <Dropdown.Section>
              <Dropdown.Item
                id="logout"
                textValue={t('nav.logout', 'Выйти')}
                className="text-danger"
                onPress={handleLogout}
              >
                <div className="flex items-center gap-2">
                  <ArrowRightFromSquare width={16} height={16} />
                  <Label className="text-danger">{t('nav.logout', 'Выйти')}</Label>
                </div>
              </Dropdown.Item>
            </Dropdown.Section>
          </Dropdown.Menu>
          <Separator />
          {version}
        </Dropdown.Popover>
      </Dropdown>
    )
  }, [user, themeMode, t, setThemeMode, handleLogout, version])

  return (
    <aside
      className={`flex-shrink-0 flex flex-col border-r transition-all duration-300 ease-in-out overflow-hidden bg-background ${dark ? 'border-white/10' : 'border-zinc-200'
        }`}
      style={{ width: sidebarCollapsed ? 0 : 240 }}
    >
      <div className="w-60 min-w-60 flex flex-col h-full">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-white/5 dark:border-white/10">
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center text-accent-foreground text-sm font-bold flex-shrink-0"
            >
              W
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm leading-tight text-foreground font-heading">
                West Continent
              </div>
              <div className="text-foreground/50 text-xs">{t('nav.bookkeeping')}</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-4">
          {NAV_GROUPS.map((group, groupIdx) => (
            <div key={groupIdx} className="space-y-1">
              {group.groupLabelKey && (
                <div className="px-3 pt-2 pb-1 text-[10px] font-bold tracking-wider text-foreground/40 uppercase">
                  {t(group.groupLabelKey)}
                </div>
              )}
              <div className="space-y-0.5">
                {group.items.map(item => (
                  <SidebarItem
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    label={t(item.labelKey)}
                    badge={item.badge ? (item.to === '/analytics' || item.to === '/warehouse' ? 'New' : pagination.total) : undefined}
                    badgeColor={item.to === '/analytics' || item.to === '/warehouse' ? 'success' : 'default'}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>


        {/* Theme & Logout */}
        <div className="px-3 py-4 border-t border-white/5 dark:border-white/10 space-y-1">
          {/* User Info */}
          {userInfo}
        </div>
      </div>
    </aside>
  )
}
